import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon, Modal, Drawer, CopyButton, Select, SelectItem } from '../components/ui';
import { usePermission } from '../hooks/usePermission';
import './McpGateway.css';

/**
 * Cloud MCP Gateway: the remote MCP servers that hosted AI assistants reach through Agenti5.
 *
 * Built for hundreds of upstreams: the browser only ever holds one page. Search, filter,
 * sort and paging happen on the server (`/admin/mcp-gateway/overview`), changes to many
 * servers go through one bulk call, and per-server detail (tools, rules) loads on demand.
 */

interface McpGatewayProps {
  apiUrl: string;
}

type Mode = 'block' | 'neutralize' | 'alert';

interface Upstream {
  slug: string;
  name: string;
  upstream_url: string;
  connector_url: string;
  auth_mode: 'passthrough' | 'broker' | 'none';
  credential_strategy: 'passthrough' | 'obo';
  upstream_scope: string | null;
  issuer: string | null;
  audience: string | null;
  jwks_url: string | null;
  userinfo_url: string | null;
  authorization_servers: string[] | null;
  injection_action: Mode;
  tool_pinning: 'enforce' | 'monitor' | 'off';
  output_dlp: 'off' | 'alert' | 'redact';
  capture: 'full' | 'metadata';
  client_agent_name: string;
  enabled: boolean;
}

interface Row extends Upstream {
  calls: number;
  users: number;
  violations: number;
  pending_tools: number;
  attention: boolean;
}

interface Grant {
  id: string;
  user_name: string | null;
  client_name: string | null;
  status: string;
  created_at: string;
  last_used: string | null;
}

interface Summary {
  upstreams: number;
  enabled: number;
  disabled: number;
  attention: number;
  calls: number;
  violations: number;
  pending_tools: number;
  users: number;
}

interface Page {
  upstreams: Row[];
  total: number;
  page: number;
  pages: number;
  pageSize: number;
  summary: Summary;
  slugs?: string[];
}

interface ToolPin {
  tool_name: string;
  status: 'approved' | 'pending' | 'blocked';
  reason: string | null;
  description: string | null;
  last_seen: string;
}

interface ToolRule {
  id: number;
  tool_pattern: string;
  principal_type: 'user' | 'group' | 'any';
  principal: string | null;
  effect: 'allow' | 'deny';
}

type Form = Omit<Upstream, 'connector_url' | 'authorization_servers'> & { authorization_servers: string };
type StatusFilter = 'all' | 'enabled' | 'disabled' | 'attention';
type SortKey = 'name' | 'calls' | 'users' | 'violations' | 'pending';

const EMPTY_FORM: Form = {
  slug: '', name: '', upstream_url: '', auth_mode: 'passthrough', credential_strategy: 'passthrough', upstream_scope: '', issuer: '', audience: '', jwks_url: '', userinfo_url: '',
  authorization_servers: '', injection_action: 'block', tool_pinning: 'monitor', output_dlp: 'off', capture: 'full',
  client_agent_name: 'Claude Enterprise', enabled: true,
};

const PAGE_SIZES = [25, 50, 100];
const PIN_BADGE: Record<string, string> = { approved: 'badge-success', pending: 'badge-warning', blocked: 'badge-error' };

async function api<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { ...init, headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) } });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.message || body.error || `Request failed (${res.status})`);
  return body as T;
}

/** Stable colour per server so a long list stays scannable. */
const hueOf = (s: string) => Array.from(s).reduce((a, c) => (a * 31 + c.charCodeAt(0)) % 360, 7);
const initials = (name: string) => {
  const w = name.replace(/[^A-Za-z0-9 ]/g, ' ').trim().split(/\s+/).filter(Boolean);
  return ((w[0]?.[0] || '?') + (w[1]?.[0] || w[0]?.[1] || '')).toUpperCase();
};
const hostOf = (u: string) => { try { return new URL(u).host; } catch { return u; } };

const McpGateway: React.FC<McpGatewayProps> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const canEdit = usePermission('configure:mcp-servers');

  // ---- list state (server-side) ----
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [injection, setInjection] = useState('all');
  const [sort, setSort] = useState<SortKey>('name');
  const [dir, setDir] = useState<'asc' | 'desc'>('asc');
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState<Page | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tick, setTick] = useState(0);
  const [toast, setToast] = useState('');
  const reqId = useRef(0);
  const searchRef = useRef<HTMLInputElement>(null);

  // ---- selection ----
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // ---- detail drawer ----
  const [open, setOpen] = useState<Row | null>(null);
  const [tab, setTab] = useState<'overview' | 'tools' | 'rules'>('overview');
  const [tools, setTools] = useState<ToolPin[] | null>(null);
  const [toolQuery, setToolQuery] = useState('');
  const [toolsShown, setToolsShown] = useState(25);
  const [rules, setRules] = useState<ToolRule[] | null>(null);
  const [newRule, setNewRule] = useState({ tool_pattern: '', principal_type: 'group', principal: '', effect: 'allow' });

  // ---- edit modal ----
  const [form, setForm] = useState<Form | null>(null);
  const [grants, setGrants] = useState<Grant[] | null>(null);
  const [brokerOk, setBrokerOk] = useState<boolean | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // debounce the search box
  useEffect(() => {
    const h = setTimeout(() => { setQ(search.trim()); setPageNo(1); }, 250);
    return () => clearTimeout(h);
  }, [search]);

  // "/" jumps to search, as in most admin tools
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (e.key === '/' && !/INPUT|TEXTAREA|SELECT/.test(el.tagName) && !el.isContentEditable) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const reload = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    const id = ++reqId.current;
    setLoading(true);
    const params = new URLSearchParams({ status, injection, sort, dir, page: String(pageNo), page_size: String(pageSize), window: '168' });
    if (q) params.set('q', q);
    api<Page>(`${apiUrl}/admin/mcp-gateway/overview?${params}`)
      .then((d) => {
        if (id !== reqId.current) return; // a newer request superseded this one
        setData(d);
        if (d.page !== pageNo) setPageNo(d.page);
        setError('');
      })
      .catch((e) => { if (id === reqId.current) setError(e.message); })
      .finally(() => { if (id === reqId.current) setLoading(false); });
  }, [apiUrl, q, status, injection, sort, dir, pageNo, pageSize, tick]);

  const flash = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  };

  const changeFilter = (fn: () => void) => { fn(); setPageNo(1); setSelected(new Set()); };
  const clearFilters = () => changeFilter(() => { setSearch(''); setQ(''); setStatus('all'); setInjection('all'); });
  const filtered = !!q || status !== 'all' || injection !== 'all';

  const toggleSort = (k: SortKey) => {
    if (sort === k) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSort(k); setDir(k === 'name' ? 'asc' : 'desc'); }
    setPageNo(1);
  };
  const sortMark = (k: SortKey) => (sort === k ? <span className="mg-sort">{dir === 'asc' ? '▲' : '▼'}</span> : null);

  // ---- selection helpers ----
  const rows = data?.upstreams || [];
  const pageSlugs = rows.map((r) => r.slug);
  const allOnPage = pageSlugs.length > 0 && pageSlugs.every((s) => selected.has(s));
  const someOnPage = pageSlugs.some((s) => selected.has(s));
  const toggleRow = (slug: string) => setSelected((s) => { const n = new Set(s); n.has(slug) ? n.delete(slug) : n.add(slug); return n; });
  const togglePage = () => setSelected((s) => { const n = new Set(s); pageSlugs.forEach((p) => (allOnPage ? n.delete(p) : n.add(p))); return n; });
  const selectAllMatching = async () => {
    try {
      const params = new URLSearchParams({ status, injection, sort, dir, ids: '1', page_size: '1' });
      if (q) params.set('q', q);
      const d = await api<Page>(`${apiUrl}/admin/mcp-gateway/overview?${params}`);
      setSelected(new Set(d.slugs || []));
    } catch (e: any) { setError(e.message); }
  };

  const bulk = async (action: 'enable' | 'disable' | 'delete' | 'set', set?: Record<string, string>) => {
    const slugs = Array.from(selected);
    if (!slugs.length) return;
    if (action === 'delete' && !window.confirm(t('pages.mcpGateway.bulkDeleteConfirm', { count: slugs.length }))) return;
    try {
      const r = await api<{ matched: number }>(`${apiUrl}/admin/mcp-gateway/upstreams/bulk`, { method: 'POST', body: JSON.stringify({ slugs, action, set }) });
      flash(t('pages.mcpGateway.bulkDone', { count: r.matched }));
      setSelected(new Set());
      reload();
    } catch (e: any) { setError(e.message); }
  };

  // ---- drawer ----
  const openDrawer = (r: Row, startTab: 'overview' | 'tools' | 'rules' = 'overview') => {
    setOpen(r); setTab(startTab); setTools(null); setRules(null); setGrants(null); setToolQuery(''); setToolsShown(25);
  };
  const loadGrants = useCallback(async (slug: string) => {
    try { setGrants((await api<{ grants: Grant[] }>(`${apiUrl}/admin/mcp-gateway/grants?slug=${slug}`)).grants); } catch (e: any) { setError(e.message); }
  }, [apiUrl]);
  const revokeGrant = async (id: string) => {
    if (!window.confirm(t('pages.mcpGateway.revokeConfirm'))) return;
    try {
      await api(`${apiUrl}/admin/mcp-gateway/grants/${id}/revoke`, { method: 'POST' });
      flash(t('pages.mcpGateway.revoked'));
      if (open) loadGrants(open.slug);
    } catch (e: any) { setError(e.message); }
  };
  const loadTools = useCallback(async (slug: string) => {
    try { setTools((await api<{ tools: ToolPin[] }>(`${apiUrl}/admin/mcp-gateway/upstreams/${slug}/tools`)).tools); } catch (e: any) { setError(e.message); }
  }, [apiUrl]);
  const loadRules = useCallback(async (slug: string) => {
    try { setRules((await api<{ rules: ToolRule[] }>(`${apiUrl}/admin/mcp-gateway/upstreams/${slug}/rules`)).rules); } catch (e: any) { setError(e.message); }
  }, [apiUrl]);
  useEffect(() => {
    if (!open) return;
    if (tab === 'tools' && tools === null) loadTools(open.slug);
    if (tab === 'rules' && rules === null) loadRules(open.slug);
    if (tab === 'overview' && open.auth_mode === 'broker' && grants === null) loadGrants(open.slug);
  }, [open, tab, tools, rules, grants, loadTools, loadRules, loadGrants]);

  // is the gateway's Entra app configured? (only matters when a connector uses brokered sign-in)
  useEffect(() => {
    if (form?.auth_mode !== 'broker' || brokerOk !== null) return;
    api<{ configured: boolean }>(`${apiUrl}/admin/mcp-gateway/broker`).then((b) => setBrokerOk(b.configured)).catch(() => undefined);
  }, [form?.auth_mode, brokerOk, apiUrl]);

  // keep the drawer's numbers fresh after a reload
  useEffect(() => {
    if (!open || !data) return;
    const fresh = data.upstreams.find((r) => r.slug === open.slug);
    if (fresh && fresh !== open) setOpen(fresh);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const setPin = async (tool: string, action: 'approve' | 'block') => {
    if (!open) return;
    try {
      await api(`${apiUrl}/admin/mcp-gateway/upstreams/${open.slug}/tools/${encodeURIComponent(tool)}/${action}`, { method: 'POST' });
      await loadTools(open.slug);
      reload();
    } catch (e: any) { setError(e.message); }
  };
  const addRule = async () => {
    if (!open) return;
    try {
      const r = await api<{ rules: ToolRule[] }>(`${apiUrl}/admin/mcp-gateway/upstreams/${open.slug}/rules`, { method: 'POST', body: JSON.stringify(newRule) });
      setRules(r.rules);
      setNewRule({ tool_pattern: '', principal_type: 'group', principal: '', effect: 'allow' });
    } catch (e: any) { setError(e.message); }
  };
  const deleteRule = async (id: number) => {
    if (!open) return;
    try { await api(`${apiUrl}/admin/mcp-gateway/rules/${id}`, { method: 'DELETE' }); await loadRules(open.slug); } catch (e: any) { setError(e.message); }
  };

  const toggleEnabled = async (r: Row) => {
    try {
      await api(`${apiUrl}/admin/mcp-gateway/upstreams/bulk`, { method: 'POST', body: JSON.stringify({ slugs: [r.slug], action: r.enabled ? 'disable' : 'enable' }) });
      reload();
    } catch (e: any) { setError(e.message); }
  };
  const remove = async (r: Row) => {
    if (!window.confirm(t('pages.mcpGateway.confirmDelete', { name: r.name }))) return;
    try { await api(`${apiUrl}/admin/mcp-gateway/upstreams/${r.slug}`, { method: 'DELETE' }); setOpen(null); reload(); } catch (e: any) { setError(e.message); }
  };
  const edit = (u: Upstream) => {
    setEditing(true);
    setForm({
      ...EMPTY_FORM, ...u,
      upstream_scope: u.upstream_scope || '', issuer: u.issuer || '', audience: u.audience || '', jwks_url: u.jwks_url || '', userinfo_url: u.userinfo_url || '',
      authorization_servers: (u.authorization_servers || []).join(', '),
    });
  };
  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const body = JSON.stringify({ ...form, authorization_servers: form.authorization_servers || null });
      if (editing) await api(`${apiUrl}/admin/mcp-gateway/upstreams/${form.slug}`, { method: 'PUT', body });
      else await api(`${apiUrl}/admin/mcp-gateway/upstreams`, { method: 'POST', body });
      setForm(null);
      reload();
    } catch (e: any) { setError(e.message); } finally { setSaving(false); }
  };

  const field = (id: keyof Form, label: string, placeholder = '', hint?: string) => (
    <div className="ent-field">
      <label htmlFor={`gw-${id}`}>{label}</label>
      <input
        id={`gw-${id}`}
        className="ent-input"
        value={String(form?.[id] ?? '')}
        disabled={editing && id === 'slug'}
        placeholder={placeholder}
        onChange={(e) => setForm((f) => (f ? { ...f, [id]: e.target.value } : f))}
      />
      {hint && <span className="muted-text" style={{ fontSize: 11 }}>{hint}</span>}
    </div>
  );
  const select = (id: keyof Form, label: string, options: string[]) => (
    <div className="ent-field">
      <label htmlFor={`gw-${id}`}>{label}</label>
      <Select id={`gw-${id}`} value={String(form?.[id] ?? '')} onValueChange={(v) => setForm((f) => (f ? { ...f, [id]: v } : f))}>
        {options.map((o) => <SelectItem key={o} value={o}>{t(`pages.mcpGateway.opt_${id}_${o}`)}</SelectItem>)}
      </Select>
    </div>
  );

  const sum = data?.summary;
  const maxCalls = Math.max(1, ...rows.map((r) => r.calls));
  const from = data && data.total ? (data.page - 1) * data.pageSize + 1 : 0;
  const to = data ? Math.min(data.page * data.pageSize, data.total) : 0;

  const tile = (key: string, value: number | string, label: string, tone: string, onClick?: () => void, sub?: string) => (
    <button key={key} className={`mg-tile mg-tone-${tone}`} onClick={onClick} disabled={!onClick} type="button">
      <span className="mg-tile-num">{value}</span>
      <span className="mg-tile-label">{label}</span>
      {sub && <span className="mg-tile-sub">{sub}</span>}
    </button>
  );

  const visibleTools = (tools || []).filter((p) => !toolQuery || `${p.tool_name} ${p.description || ''}`.toLowerCase().includes(toolQuery.toLowerCase()))
    .sort((a, b) => (a.status === 'pending' ? -1 : 0) - (b.status === 'pending' ? -1 : 0) || a.tool_name.localeCompare(b.tool_name));

  return (
    <div className="page-content mg-root">
      <div className="page-title">
        <div>
          <h1><Icon name="shield" size={20} /> {t('pages.mcpGateway.title')}</h1>
          <p className="subtitle">{t('pages.mcpGateway.subtitle')}</p>
          <p className="subtitle"><Link to="/mcp-servers">{t('pages.mcpGateway.seeServers')}</Link></p>
        </div>
        <div className="ent-toolbar" style={{ margin: 0 }}>
          {canEdit && (
            <button className="ent-btn primary" onClick={() => { setEditing(false); setForm({ ...EMPTY_FORM }); }}>
              <Icon name="plus" /> {t('pages.mcpGateway.add')}
            </button>
          )}
          <button className="ent-btn" onClick={reload} disabled={loading}>
            <Icon name="refresh" className={loading ? 'spin' : ''} /> {t('pages.mcpGateway.refresh')}
          </button>
        </div>
      </div>

      {error && <div className="notice error"><Icon name="warning" /> {error}</div>}
      {toast && <div className="mg-toast" role="status"><Icon name="checkCircle" size={16} /> {toast}</div>}

      <div className="mg-tiles">
        {tile('up', sum?.upstreams ?? '–', t('pages.mcpGateway.statUpstreams'), 'neutral', () => clearFilters(), sum ? t('pages.mcpGateway.statEnabled', { count: sum.enabled }) : undefined)}
        {tile('calls', sum?.calls ?? '–', t('pages.mcpGateway.statCalls'), 'neutral')}
        {tile('users', sum?.users ?? '–', t('pages.mcpGateway.statUsers'), 'neutral')}
        {tile('viol', sum?.violations ?? '–', t('pages.mcpGateway.statViolations'), sum?.violations ? 'red' : 'green',
          () => changeFilter(() => { setStatus('attention'); setSort('violations'); setDir('desc'); }))}
        {tile('pend', sum?.pending_tools ?? '–', t('pages.mcpGateway.statPending'), sum?.pending_tools ? 'amber' : 'green',
          () => changeFilter(() => { setStatus('attention'); setSort('pending'); setDir('desc'); }))}
      </div>

      <details className="mg-how" open={!!data && data.summary.upstreams === 0}>
        <summary><Icon name="info" size={14} /> {t('pages.mcpGateway.howTitle')}</summary>
        <ol>
          <li>{t('pages.mcpGateway.how1')}</li>
          <li>{t('pages.mcpGateway.how2')}</li>
          <li>{t('pages.mcpGateway.how3')}</li>
        </ol>
      </details>

      <div className="mg-toolbar">
        <div className="mg-search">
          <Icon name="search" size={15} />
          <input
            ref={searchRef}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setSelected(new Set()); }}
            placeholder={t('pages.mcpGateway.searchPlaceholder')}
            aria-label={t('pages.mcpGateway.searchPlaceholder')}
          />
          {search ? <button className="mg-clear" onClick={() => { setSearch(''); setQ(''); }} aria-label={t('pages.mcpGateway.clear')}><Icon name="close" size={13} /></button> : <kbd>/</kbd>}
        </div>
        <div className="mg-chips" role="tablist">
          {([['all', sum?.upstreams], ['enabled', sum?.enabled], ['disabled', sum?.disabled], ['attention', sum?.attention]] as Array<[StatusFilter, number | undefined]>).map(([k, n]) => (
            <button key={k} role="tab" aria-selected={status === k} className={`mg-chip ${status === k ? 'on' : ''} ${k === 'attention' && n ? 'warn' : ''}`}
              onClick={() => changeFilter(() => setStatus(k))}>
              {t(`pages.mcpGateway.filter_${k}`)} <span className="mg-chip-n">{n ?? '–'}</span>
            </button>
          ))}
        </div>
        <div className="mg-select">
          <Select value={injection} onValueChange={(v) => changeFilter(() => setInjection(v))}>
            <SelectItem value="all">{t('pages.mcpGateway.injectionAny')}</SelectItem>
            {(['block', 'neutralize', 'alert'] as Mode[]).map((m) => (
              <SelectItem key={m} value={m}>{t('pages.mcpGateway.fInjection')}: {t(`pages.mcpGateway.opt_injection_action_${m}`)}</SelectItem>
            ))}
          </Select>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="mg-bulk" role="region" aria-label={t('pages.mcpGateway.bulkBar')}>
          <strong>{t('pages.mcpGateway.selected', { count: selected.size })}</strong>
          {data && selected.size < data.total && allOnPage && (
            <button className="mg-link" onClick={selectAllMatching}>{t('pages.mcpGateway.selectAllMatching', { count: data.total })}</button>
          )}
          <span className="mg-spacer" />
          {canEdit && (
            <>
              <button className="ent-btn sm" onClick={() => bulk('enable')}>{t('pages.mcpGateway.enable')}</button>
              <button className="ent-btn sm" onClick={() => bulk('disable')}>{t('pages.mcpGateway.disable')}</button>
              <div className="mg-bulk-select">
                <Select value="" placeholder={t('pages.mcpGateway.setInjection')} onValueChange={(v) => bulk('set', { injection_action: v })}>
                  {(['block', 'neutralize', 'alert'] as Mode[]).map((m) => <SelectItem key={m} value={m}>{t(`pages.mcpGateway.opt_injection_action_${m}`)}</SelectItem>)}
                </Select>
              </div>
              <div className="mg-bulk-select">
                <Select value="" placeholder={t('pages.mcpGateway.setPinning')} onValueChange={(v) => bulk('set', { tool_pinning: v })}>
                  {(['enforce', 'monitor', 'off'] as const).map((m) => <SelectItem key={m} value={m}>{t(`pages.mcpGateway.opt_tool_pinning_${m}`)}</SelectItem>)}
                </Select>
              </div>
              <button className="ent-btn sm ghost" onClick={() => bulk('delete')}><Icon name="trash" /> {t('pages.mcpGateway.delete')}</button>
            </>
          )}
          <button className="ent-btn sm ghost" onClick={() => setSelected(new Set())}>{t('pages.mcpGateway.clear')}</button>
        </div>
      )}

      <div className={`mg-table-wrap ${loading && data ? 'is-loading' : ''}`}>
        <table className="mg-table">
          <thead>
            <tr>
              <th className="mg-col-check">
                <input type="checkbox" aria-label={t('pages.mcpGateway.selectPage')} checked={allOnPage} ref={(el) => { if (el) el.indeterminate = !allOnPage && someOnPage; }} onChange={togglePage} />
              </th>
              <th><button className="mg-th" onClick={() => toggleSort('name')}>{t('pages.mcpGateway.name')}{sortMark('name')}</button></th>
              <th>{t('pages.mcpGateway.protection')}</th>
              <th className="mg-num"><button className="mg-th" onClick={() => toggleSort('calls')}>{t('pages.mcpGateway.calls7d')}{sortMark('calls')}</button></th>
              <th className="mg-num"><button className="mg-th" onClick={() => toggleSort('users')}>{t('pages.mcpGateway.users')}{sortMark('users')}</button></th>
              <th className="mg-num"><button className="mg-th" onClick={() => toggleSort('violations')}>{t('pages.mcpGateway.violations')}{sortMark('violations')}</button></th>
              <th className="mg-num"><button className="mg-th" onClick={() => toggleSort('pending')}>{t('pages.mcpGateway.pendingTools')}{sortMark('pending')}</button></th>
              <th className="mg-col-act" />
            </tr>
          </thead>
          <tbody>
            {!data && Array.from({ length: 8 }).map((_, i) => (
              <tr key={i} className="mg-skel"><td colSpan={8}><span /></td></tr>
            ))}
            {data && rows.length === 0 && (
              <tr><td colSpan={8} className="mg-empty">
                {filtered ? (
                  <>
                    <Icon name="search" size={22} />
                    <p>{t('pages.mcpGateway.noMatches')}</p>
                    <button className="ent-btn sm" onClick={clearFilters}>{t('pages.mcpGateway.clearFilters')}</button>
                  </>
                ) : (
                  <>
                    <Icon name="shield" size={22} />
                    <p>{t('pages.mcpGateway.empty')}</p>
                    {canEdit && <button className="ent-btn primary sm" onClick={() => { setEditing(false); setForm({ ...EMPTY_FORM }); }}><Icon name="plus" /> {t('pages.mcpGateway.add')}</button>}
                  </>
                )}
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.slug} className={`mg-row ${selected.has(r.slug) ? 'sel' : ''} ${r.enabled ? '' : 'off'}`}
                tabIndex={0} onClick={() => openDrawer(r)} onKeyDown={(e) => { if (e.key === 'Enter') openDrawer(r); }}>
                <td className="mg-col-check" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" aria-label={r.name} checked={selected.has(r.slug)} onChange={() => toggleRow(r.slug)} />
                </td>
                <td>
                  <div className="mg-name">
                    <span className="mg-avatar" style={{ background: `hsl(${hueOf(r.slug)} 42% 42%)` }}>{initials(r.name)}</span>
                    <span className="mg-name-text">
                      <span className="mg-name-main">
                        {r.name}
                        {!r.enabled && <span className="mg-pill off">{t('pages.mcpGateway.disabled')}</span>}
                      </span>
                      <span className="mg-name-sub">{r.slug} · {hostOf(r.upstream_url)}</span>
                    </span>
                  </div>
                </td>
                <td>
                  <span className={`mg-mode mg-mode-${r.injection_action}`} title={`${t('pages.mcpGateway.fInjection')}: ${t(`pages.mcpGateway.opt_injection_action_${r.injection_action}`)}`}>{t(`pages.mcpGateway.modeShort_${r.injection_action}`)}</span>
                  <span className="mg-mode mg-mode-plain" title={`${t('pages.mcpGateway.fPinning')}: ${t(`pages.mcpGateway.opt_tool_pinning_${r.tool_pinning}`)}`}><Icon name="lock" size={11} /> {t(`pages.mcpGateway.opt_tool_pinning_${r.tool_pinning}`)}</span>
                </td>
                <td className="mg-num">
                  <span className="mg-bar"><i style={{ width: `${Math.round((r.calls / maxCalls) * 100)}%` }} /></span>
                  <span className="mono">{r.calls.toLocaleString()}</span>
                </td>
                <td className="mg-num mono">{r.users}</td>
                <td className="mg-num">{r.violations ? <span className="mg-pill red">{r.violations}</span> : <span className="mg-zero">0</span>}</td>
                <td className="mg-num">{r.pending_tools ? <span className="mg-pill amber">{r.pending_tools}</span> : <span className="mg-zero">0</span>}</td>
                <td className="mg-col-act" onClick={(e) => e.stopPropagation()}>
                  <CopyButton value={r.connector_url} title={t('pages.mcpGateway.copyConnector')} />
                  <button className="mg-open" onClick={() => openDrawer(r)} aria-label={t('pages.mcpGateway.open')}><Icon name="chevronRight" size={16} mirrorInRtl /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data && data.total > 0 && (
        <div className="mg-pager">
          <span className="muted-text">{t('pages.mcpGateway.rowsRange', { from, to, total: data.total })}</span>
          <span className="mg-spacer" />
          <label className="mg-size">
            {t('pages.mcpGateway.perPage')}
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPageNo(1); }}>
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <button className="ent-btn sm" disabled={data.page <= 1} onClick={() => setPageNo(data.page - 1)}>{t('pages.mcpGateway.prev')}</button>
          <span className="mono">{data.page} / {data.pages}</span>
          <button className="ent-btn sm" disabled={data.page >= data.pages} onClick={() => setPageNo(data.page + 1)}>{t('pages.mcpGateway.next')}</button>
        </div>
      )}

      {open && (
        <Drawer title={open.name} onClose={() => setOpen(null)} width={600}>
          <div className="mg-drawer">
            <div className="mg-drawer-head">
              <span className="mg-avatar lg" style={{ background: `hsl(${hueOf(open.slug)} 42% 42%)` }}>{initials(open.name)}</span>
              <div>
                <div className="mono mg-name-sub">{open.slug}</div>
                <span className={`mg-pill ${open.enabled ? 'green' : 'off'}`}>{open.enabled ? t('pages.mcpGateway.enabled') : t('pages.mcpGateway.disabled')}</span>
              </div>
              {canEdit && (
                <div className="mg-drawer-actions">
                  <button className="ent-btn sm" onClick={() => toggleEnabled(open)}>{open.enabled ? t('pages.mcpGateway.disable') : t('pages.mcpGateway.enable')}</button>
                  <button className="ent-btn sm" onClick={() => edit(open)}>{t('pages.mcpGateway.edit')}</button>
                  <button className="ent-btn sm ghost" title={t('pages.mcpGateway.delete')} onClick={() => remove(open)}><Icon name="trash" /></button>
                </div>
              )}
            </div>

            <div className="mg-tabs" role="tablist">
              {(['overview', 'tools', 'rules'] as const).map((k) => (
                <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>
                  {t(`pages.mcpGateway.tab_${k}`)}
                  {k === 'tools' && open.pending_tools > 0 && <span className="mg-pill amber">{open.pending_tools}</span>}
                </button>
              ))}
            </div>

            {tab === 'overview' && (
              <div className="mg-pane">
                <label className="mg-label">{t('pages.mcpGateway.connectorUrl')}</label>
                <div className="mg-url"><span className="mono">{open.connector_url}</span><CopyButton value={open.connector_url} title={t('pages.mcpGateway.copyConnector')} /></div>
                <label className="mg-label">{t('pages.mcpGateway.fUpstream')}</label>
                <div className="mg-url"><span className="mono">{open.upstream_url}</span><CopyButton value={open.upstream_url} /></div>
                <div className="mg-facts">
                  <div><label>{t('pages.mcpGateway.fInjection')}</label><span className={`mg-mode mg-mode-${open.injection_action}`}>{t(`pages.mcpGateway.opt_injection_action_${open.injection_action}`)}</span></div>
                  <div><label>{t('pages.mcpGateway.fPinning')}</label>{t(`pages.mcpGateway.opt_tool_pinning_${open.tool_pinning}`)}</div>
                  <div><label>{t('pages.mcpGateway.fOutputDlp')}</label>{t(`pages.mcpGateway.opt_output_dlp_${open.output_dlp}`)}</div>
                  <div><label>{t('pages.mcpGateway.fCapture')}</label>{t(`pages.mcpGateway.opt_capture_${open.capture}`)}</div>
                  <div><label>{t('pages.mcpGateway.fAuthMode')}</label>{t(`pages.mcpGateway.opt_auth_mode_${open.auth_mode}`)}</div>
                  <div><label>{t('pages.mcpGateway.fClient')}</label>{open.client_agent_name}</div>
                </div>
                {open.auth_mode === 'broker' && (
                  <>
                    <label className="mg-label" style={{ marginTop: 14 }}>{t('pages.mcpGateway.connectedUsers')}</label>
                    {grants === null ? <div className="mg-skel-block" /> : grants.length === 0 ? (
                      <p className="muted-text" style={{ margin: 0 }}>{t('pages.mcpGateway.noConnected')}</p>
                    ) : (
                      <ul className="mg-list">
                        {grants.slice(0, 50).map((g) => (
                          <li key={g.id}>
                            <div className="mg-list-main">
                              <span className="mono">{g.user_name || g.id}</span>
                              <span className={`badge ${g.status === 'active' ? 'badge-success' : 'badge-error'}`}>{g.status === 'active' ? t('pages.mcpGateway.grantActive') : t('pages.mcpGateway.grantRevoked')}</span>
                              <div className="mg-list-desc">{g.client_name || '—'} · {g.last_used ? t('pages.mcpGateway.lastUsed', { when: new Date(g.last_used).toLocaleString() }) : t('pages.mcpGateway.neverUsed')}</div>
                            </div>
                            {canEdit && g.status === 'active' && <div className="mg-list-act"><button className="ent-btn sm" onClick={() => revokeGrant(g.id)}>{t('pages.mcpGateway.revoke')}</button></div>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
                <div className="mg-stats">
                  <div><b>{open.calls.toLocaleString()}</b><span>{t('pages.mcpGateway.calls7d')}</span></div>
                  <div><b>{open.users}</b><span>{t('pages.mcpGateway.users')}</span></div>
                  <div className={open.violations ? 'red' : ''}><b>{open.violations}</b><span>{t('pages.mcpGateway.violations')}</span></div>
                  <div className={open.pending_tools ? 'amber' : ''}><b>{open.pending_tools}</b><span>{t('pages.mcpGateway.pendingTools')}</span></div>
                </div>
              </div>
            )}

            {tab === 'tools' && (
              <div className="mg-pane">
                <p className="muted-text" style={{ marginTop: 0 }}>{t('pages.mcpGateway.toolsHint')}</p>
                {tools === null ? <div className="mg-skel-block" /> : tools.length === 0 ? (
                  <p className="muted-text">{t('pages.mcpGateway.noTools')}</p>
                ) : (
                  <>
                    <div className="mg-search sm">
                      <Icon name="search" size={14} />
                      <input value={toolQuery} onChange={(e) => { setToolQuery(e.target.value); setToolsShown(25); }} placeholder={t('pages.mcpGateway.toolsSearch', { count: tools.length })} />
                    </div>
                    <ul className="mg-list">
                      {visibleTools.slice(0, toolsShown).map((p) => (
                        <li key={p.tool_name}>
                          <div className="mg-list-main">
                            <span className="mono">{p.tool_name}</span>
                            <span className={`badge ${PIN_BADGE[p.status]}`}>{t(`pages.mcpGateway.pin_${p.status}`)}</span>
                            {p.description && <div className="mg-list-desc">{p.description.slice(0, 220)}</div>}
                            {p.reason && <div className="mg-list-desc">{p.reason}</div>}
                          </div>
                          {canEdit && (
                            <div className="mg-list-act">
                              {p.status !== 'approved' && <button className="ent-btn sm" onClick={() => setPin(p.tool_name, 'approve')}>{t('pages.mcpGateway.approve')}</button>}
                              {p.status !== 'blocked' && <button className="ent-btn sm ghost" onClick={() => setPin(p.tool_name, 'block')}>{t('pages.mcpGateway.block')}</button>}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                    {visibleTools.length > toolsShown && (
                      <button className="ent-btn sm" onClick={() => setToolsShown((n) => n + 50)}>{t('pages.mcpGateway.showMore', { count: visibleTools.length - toolsShown })}</button>
                    )}
                    {visibleTools.length === 0 && <p className="muted-text">{t('pages.mcpGateway.noMatches')}</p>}
                  </>
                )}
              </div>
            )}

            {tab === 'rules' && (
              <div className="mg-pane">
                <p className="muted-text" style={{ marginTop: 0 }}>{t('pages.mcpGateway.rulesHint')}</p>
                {rules === null ? <div className="mg-skel-block" /> : rules.length === 0 ? (
                  <p className="muted-text">{t('pages.mcpGateway.noRules')}</p>
                ) : (
                  <ul className="mg-list">
                    {rules.map((r) => (
                      <li key={r.id}>
                        <div className="mg-list-main">
                          <span className="mono">{r.tool_pattern}</span>
                          <span className={`badge ${r.effect === 'allow' ? 'badge-success' : 'badge-error'}`}>{t(`pages.mcpGateway.effect_${r.effect}`)}</span>
                          <div className="mg-list-desc">{r.principal_type === 'any' ? t('pages.mcpGateway.everyone') : `${t(`pages.mcpGateway.ptype_${r.principal_type}`)}: ${r.principal}`}</div>
                        </div>
                        {canEdit && <div className="mg-list-act"><button className="ent-btn sm ghost" onClick={() => deleteRule(r.id)} aria-label={t('pages.mcpGateway.delete')}><Icon name="trash" /></button></div>}
                      </li>
                    ))}
                  </ul>
                )}
                {canEdit && (
                  <div className="mg-rule-form">
                    <div className="ent-field">
                      <label htmlFor="rule-tool">{t('pages.mcpGateway.tool')}</label>
                      <input id="rule-tool" className="ent-input" placeholder="get_salary*" value={newRule.tool_pattern} onChange={(e) => setNewRule({ ...newRule, tool_pattern: e.target.value })} />
                    </div>
                    <div className="ent-field">
                      <label htmlFor="rule-effect">{t('pages.mcpGateway.effect')}</label>
                      <Select id="rule-effect" value={newRule.effect} onValueChange={(v) => setNewRule({ ...newRule, effect: v })}>
                        <SelectItem value="allow">{t('pages.mcpGateway.effect_allow')}</SelectItem>
                        <SelectItem value="deny">{t('pages.mcpGateway.effect_deny')}</SelectItem>
                      </Select>
                    </div>
                    <div className="ent-field">
                      <label htmlFor="rule-ptype">{t('pages.mcpGateway.who')}</label>
                      <Select id="rule-ptype" value={newRule.principal_type} onValueChange={(v) => setNewRule({ ...newRule, principal_type: v })}>
                        <SelectItem value="group">{t('pages.mcpGateway.ptype_group')}</SelectItem>
                        <SelectItem value="user">{t('pages.mcpGateway.ptype_user')}</SelectItem>
                        <SelectItem value="any">{t('pages.mcpGateway.everyone')}</SelectItem>
                      </Select>
                    </div>
                    <div className="ent-field">
                      <label htmlFor="rule-principal">{t('pages.mcpGateway.principal')}</label>
                      <input id="rule-principal" className="ent-input" disabled={newRule.principal_type === 'any'}
                        placeholder={newRule.principal_type === 'user' ? 'alice@company.com' : 'Finance'}
                        value={newRule.principal} onChange={(e) => setNewRule({ ...newRule, principal: e.target.value })} />
                    </div>
                    <button className="ent-btn primary" onClick={addRule} disabled={!newRule.tool_pattern}><Icon name="plus" /> {t('pages.mcpGateway.addRule')}</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </Drawer>
      )}

      {form && (
        <Modal
          title={editing ? t('pages.mcpGateway.editTitle', { name: form.name }) : t('pages.mcpGateway.addTitle')}
          icon="shield"
          maxWidth={720}
          onClose={() => setForm(null)}
          footer={
            <>
              <button className="ent-btn" onClick={() => setForm(null)}>{t('pages.mcpGateway.cancel')}</button>
              <button className="ent-btn primary" onClick={save} disabled={saving}>{t('pages.mcpGateway.save')}</button>
            </>
          }
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
            {field('name', t('pages.mcpGateway.fName'), 'Oracle Fusion HCM')}
            {field('slug', t('pages.mcpGateway.fSlug'), 'oracle-hcm', t('pages.mcpGateway.fSlugHint'))}
          </div>
          {field('upstream_url', t('pages.mcpGateway.fUpstream'), 'https://mcp.oraclecloud.com/…/mcp', t('pages.mcpGateway.fUpstreamHint'))}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
            {select('auth_mode', t('pages.mcpGateway.fAuthMode'), ['passthrough', 'broker', 'none'])}
            {field('client_agent_name', t('pages.mcpGateway.fClient'), 'Claude Enterprise')}
            {select('injection_action', t('pages.mcpGateway.fInjection'), ['block', 'neutralize', 'alert'])}
            {select('tool_pinning', t('pages.mcpGateway.fPinning'), ['monitor', 'enforce', 'off'])}
            {select('output_dlp', t('pages.mcpGateway.fOutputDlp'), ['off', 'alert', 'redact'])}
            {select('capture', t('pages.mcpGateway.fCapture'), ['full', 'metadata'])}
          </div>
          {form.auth_mode === 'broker' && (
            <div style={{ marginTop: 6 }}>
              {brokerOk === false && <div className="notice error">{t('pages.mcpGateway.brokerNotConfigured')}</div>}
              <p className="muted-text" style={{ fontSize: 12, margin: '0 0 8px' }}>{t('pages.mcpGateway.brokerHint')}</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
                {select('credential_strategy', t('pages.mcpGateway.fCredStrategy'), ['passthrough', 'obo'])}
                {field('upstream_scope', t('pages.mcpGateway.fUpstreamScope'), 'api://<upstream-app-id>/mcp.access', t('pages.mcpGateway.fUpstreamScopeHint'))}
              </div>
            </div>
          )}
          {(form.auth_mode === 'passthrough' || form.auth_mode === 'broker') && (
            <details style={{ marginTop: 6 }}>
              <summary style={{ cursor: 'pointer', fontSize: 13 }}>{t('pages.mcpGateway.fIdentity')}</summary>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px', marginTop: 8 }}>
                {field('jwks_url', t('pages.mcpGateway.fJwks'), 'https://login.microsoftonline.com/<tenant>/discovery/v2.0/keys', t('pages.mcpGateway.fJwksHint'))}
                {field('issuer', t('pages.mcpGateway.fIssuer'), 'https://login.microsoftonline.com/<tenant>/v2.0')}
                {field('audience', t('pages.mcpGateway.fAudience'))}
                {field('userinfo_url', t('pages.mcpGateway.fUserinfo'), '', t('pages.mcpGateway.fUserinfoHint'))}
              </div>
              {field('authorization_servers', t('pages.mcpGateway.fAs'), '', t('pages.mcpGateway.fAsHint'))}
            </details>
          )}
        </Modal>
      )}
    </div>
  );
};

export default McpGateway;
